import { useApiFetch } from './api-client';

export interface ChatMessage {
   message: string;
   context?: Record<string, unknown> | null;
   session_id?: string | null;
   docids?: string[] | null;
   thread_id?: number | null;
}

export interface PromptLlmOptions {
   chatMessage: ChatMessage;
   signal?: AbortSignal;
}

interface LlmChatEnvelope {
   data: { response?: string };
   meta: Record<string, unknown>;
}

export async function promptLlm<T = unknown>(options: PromptLlmOptions): Promise<T> {
   const { chatMessage, signal } = options;

   return useApiFetch<T>('/llm/chat', {
      method: 'POST',
      body: chatMessage,
      signal,
   });
}

const TITLE_PROMPT = `Generate a short title (max 8 words) for a chat thread based on this user question. Respond with only the title, no quotes or punctuation around it:

`;

/* ---------------------------------------------------------------------------------------------- */
/* Query intent classification (ENG-436)                                                           */

/** Which of the two interfaces should service a given query. */
export type QueryIntent = 'chat' | 'search';

export interface QueryClassification {
   type: QueryIntent;
   /** Keyword query to run when `type` is `search` — refined from the raw input. */
   search_query: string;
}

const CLASSIFY_PROMPT = `You route a user's input to one of two interfaces in an environmental-regulatory document platform.

"search" — the user wants a list of documents. Keywords, facility or party names, permit or EPA identifiers, document types, places, date ranges.
"chat" — the user wants a written answer. Questions, comparisons, summaries, timelines, explanations, anything phrased as a request for analysis.

Naming a body of work to be read and synthesised — a history, summary, timeline, trend, overview, or background — is "chat" even when the input also names a facility, an identifier, a document type, or a date range. Those are the subject of the answer, not a request for a list.

When an input reads plausibly either way, answer "chat": a written answer cites the documents it used, so it contains the list, while a list of documents cannot contain the answer.

Respond with ONLY a JSON object, no prose and no code fences:
{"type": "chat" | "search", "search_query": "<keyword query>"}

"search_query" must always be present. Rewrite the input into a concise keyword query suitable for a full-text document search: drop filler and question phrasing, keep proper nouns, identifiers and dates. Never invent terms the input does not imply.

Input:
`;

/**
 * Extract the first balanced-looking JSON object from a model response.
 * Models occasionally wrap JSON in prose or a code fence despite instructions.
 */
function extractJsonObject(raw: string): unknown {
   const start = raw.indexOf('{');
   const end = raw.lastIndexOf('}');
   if (start === -1 || end === -1 || end < start)
      return null;
   try {
      return JSON.parse(raw.slice(start, end + 1));
   }
   catch {
      return null;
   }
}

/**
 * Ask the LLM whether a query is best serviced by search or by chat, and get
 * back a refined keyword query to use if it's a search.
 *
 * Returns `null` whenever the answer can't be trusted (empty input, transport
 * failure, unparseable response, unknown `type`) so callers fall back to their
 * own heuristics rather than guessing from a malformed verdict.
 */
/**
 * How long a routing decision may take before the local heuristics win.
 *
 * This call gates navigation: `useQueryNavigation` holds `isRouting` true for
 * its whole duration, so a stalled endpoint means a submit button that spins
 * with no upper bound. `useApiFetch` sets no timeout of its own and retries 500s
 * three times, which makes a bad backend slower still. Returning null is a
 * supported outcome — `resolveQueryRoute` falls back to the heuristics — so
 * giving up is strictly better than waiting.
 */
const CLASSIFY_TIMEOUT_MS = 8000;

export async function classifyQuery(
   question: string,
   signal?: AbortSignal,
): Promise<QueryClassification | null> {
   const trimmed = question.trim();
   if (!trimmed)
      return null;

   try {
      const result = await promptLlm<LlmChatEnvelope>({
         chatMessage: { message: CLASSIFY_PROMPT + trimmed },
         signal: signal ?? AbortSignal.timeout(CLASSIFY_TIMEOUT_MS),
      });
      const raw = result.data?.response?.trim();
      if (!raw)
         return null;

      const parsed = extractJsonObject(raw) as Partial<QueryClassification> | null;
      if (!parsed || (parsed.type !== 'chat' && parsed.type !== 'search'))
         return null;

      const refined = typeof parsed.search_query === 'string' ? parsed.search_query.trim() : '';
      return {
         type: parsed.type,
         // A blank/missing refinement is recoverable — the raw input is always
         // a usable search query, just a less precise one.
         search_query: refined || trimmed,
      };
   }
   catch {
      return null;
   }
}

export async function generateThreadTitle(question: string): Promise<string | null> {
   if (!question.trim())
      return null;

   try {
      const result = await promptLlm<LlmChatEnvelope>({
         chatMessage: { message: TITLE_PROMPT + question },
      });
      const raw = result.data?.response?.trim();
      if (!raw)
         return null;
      // Strip surrounding quotes if present
      return raw.replace(/^["']|["']$/g, '');
   }
   catch {
      return null;
   }
}
